import * as React from "react";
import {
    ThemeInstallConfigurationResource,
    themeInstallConfigurationGraphQlToResource,
} from "@spiffcommerce/theme-bridge";
import { lazy, useEffect, useState } from "react";
import FullScreenSpinner from "../../ApplicationIndex/components/FullScreenSpinner";
import { Params, useParams } from "react-router-dom";
import qs, { ParsedQs } from "qs";
import { Currency } from "dinero.js";
import type {
    LoadMarketplaceThemeOptions,
    LoadMarketplaceThemeOptionsBundle,
    LoadMarketplaceThemeOptionsTransaction,
} from "./provider";
import {
    getBundleThemeConfiguration,
    getIntegration,
    getOverrideThemeConfiguration,
    getTransactionThemeConfiguration,
} from "@spiffcommerce/core";
import { ErrorPage } from "../../ApplicationIndex/MiscPages";
const MarketplaceThemeProvider = lazy(() => import("./provider"));

export const newTransactionWorkflowOptionsBuilder = (
    _routeParams: Readonly<Params<string>>,
    queryParams: ParsedQs,
): LoadMarketplaceThemeOptionsTransaction => {
    const integrationProductId = queryParams["integrationProductId"] as string | undefined;
    const externalIntegrationId = queryParams["externalIntegrationId"] as string | undefined;
    const externalProductId = queryParams["externalProductId"] as string | undefined;
    const workflowId = queryParams["workflowId"] as string | undefined;
    const designName = queryParams["name"] as string | undefined;
    if (integrationProductId) {
        return {
            options: {
                integrationProductId,
                workflowId,
                designName,
                type: "integration",
            },
            type: "transaction",
        };
    }
    if (externalIntegrationId && externalProductId) {
        return {
            options: {
                externalIntegrationId,
                externalProductId,
                workflowId,
                designName,
                type: "external",
            },
            type: "transaction",
        };
    }
    throw new Error("Invalid launch parameters.");
};

export const continueTransactionWorkflowOptionsBuilder = (
    routeParams: Readonly<Params<string>>,
): LoadMarketplaceThemeOptionsTransaction => {
    return {
        options: {
            transactionId: routeParams.transactionId!,
            type: "transaction",
        },
        type: "transaction",
    };
};

export const newBundleOptionsBuilder = (
    _routeParams: Readonly<Params<string>>,
    queryParams: ParsedQs,
): LoadMarketplaceThemeOptionsBundle => {
    return {
        options: {
            productCollectionId: queryParams["productCollectionId"] as string | undefined,
            integrationProductId: queryParams["integrationProductId"] as string | undefined,
            type: "new",
        },
        type: "bundle",
    };
};

export const existingBundleOptionsBuilder = (
    routeParams: Readonly<Params<string>>,
): LoadMarketplaceThemeOptionsBundle => {
    return {
        options: {
            bundleId: routeParams.bundleId!,
            type: "existing",
        },
        type: "bundle",
    };
};

export enum ConfigurationError {
    NO_APPLICATION_KEY = "NO_APPLICATION_KEY",
    NO_THEME = "NO_THEME",
    INVALID_LAUNCH_PARAMS = "INVALID_LAUNCH_PARAMS",
}

export const withMarketplaceTheme = (
    getOptions: (routeParams: Readonly<Params<string>>, queryParams: ParsedQs) => LoadMarketplaceThemeOptions,
    Fallback?: React.FunctionComponent,
) => {
    // eslint-disable-next-line react/display-name
    return () => {
        const params = useParams();
        const queryStringParams = qs.parse(window.location.hash.split("?")[1], { ignoreQueryPrefix: true });
        const [applicationKey] = useState(queryStringParams["applicationKey"] as string | undefined);
        const [presentmentCurrency] = useState(queryStringParams["currencyCode"] as Currency | undefined);
        const [overrideThemeInstallId] = useState(queryStringParams["overrideThemeInstallId"] as string | undefined);
        const [overrideThemeConfigurationId] = useState(
            queryStringParams["overrideThemeConfigurationId"] as string | undefined,
        );
        const [fromCustomerPortal] = useState(queryStringParams["fromCustomerPortal"] as string | undefined);
        const [configuration, setConfiguration] = useState<ThemeInstallConfigurationResource | undefined>(undefined);
        const [loading, setLoading] = useState(true);
        const [partnerConfigError, setPartnerConfigError] = useState<ConfigurationError | undefined>(undefined);
        const [options, setOptions] = useState<LoadMarketplaceThemeOptions | undefined>();

        useEffect(() => {
            const fetch = async () => {
                // assign getOptions to ref inside fetch so if an exception throws we can handle the error
                const options = getOptions(params, queryStringParams);
                setOptions(options);

                // Not able to resolve currentIntegration without an application key
                if (!applicationKey) return { error: ConfigurationError.NO_APPLICATION_KEY };

                // If an overriding theme has been specified, pull this first.
                if (overrideThemeInstallId && overrideThemeConfigurationId) {
                    const theme = await getOverrideThemeConfiguration(
                        overrideThemeConfigurationId,
                        overrideThemeInstallId,
                    );
                    if (theme) {
                        return { data: theme };
                    }
                }

                // Try to resolve the theme from the transaction
                if (options.type === "transaction" && options.options.type === "transaction") {
                    const theme = await getTransactionThemeConfiguration(options.options.transactionId);
                    if (theme) {
                        return { data: theme };
                    }
                }

                // Try to resolve the theme from the bundle
                if (options.type === "bundle" && options.options.type === "existing") {
                    const theme = await getBundleThemeConfiguration(options.options.bundleId);
                    if (theme) {
                        return { data: theme };
                    }
                }

                // Pull information about the integration
                const currentIntegration = await getIntegration(applicationKey, overrideThemeConfigurationId);
                if (currentIntegration?.marketplaceThemeInstallConfiguration) {
                    return { data: currentIntegration?.marketplaceThemeInstallConfiguration };
                }
                return { error: ConfigurationError.NO_THEME };
            };

            fetch()
                .then((value) => {
                    if (value.error) setPartnerConfigError(value.error);
                    if (value.data) setConfiguration(themeInstallConfigurationGraphQlToResource(value.data));
                })
                .catch(() => {
                    console.log("error");
                    setPartnerConfigError(ConfigurationError.INVALID_LAUNCH_PARAMS);
                })
                .finally(() => setLoading(false));
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [applicationKey, overrideThemeConfigurationId, overrideThemeInstallId]);

        if (partnerConfigError) {
            if (Fallback) return <Fallback />;
            const getTitle = () => {
                switch (partnerConfigError) {
                    case ConfigurationError.NO_APPLICATION_KEY:
                        return "Application Key Missing";
                    case ConfigurationError.NO_THEME:
                        return "Theme Configuration Issue";
                    case ConfigurationError.INVALID_LAUNCH_PARAMS:
                        return "Invalid Launch Parameters";
                    default:
                        return undefined;
                }
            };
            const getDescription = () => {
                switch (partnerConfigError) {
                    case ConfigurationError.NO_APPLICATION_KEY:
                        return "It looks like the store owner has forgotten to configure the application key. We'll notify them of the issue! Please check back later.";
                    case ConfigurationError.NO_THEME:
                        return "It looks like no theme is configured for the requested product personalization experience. We'll notify the store owner that they have missed some steps in configuring the experience! Please try again later.";
                    case ConfigurationError.INVALID_LAUNCH_PARAMS:
                        return "It looks like something went wrong launching this experience. Typically this means a product integration doesn't exist for the product you are launching for. We'll notify the store owner of the issue! Please try again later.";
                    default:
                        return undefined;
                }
            };
            return <ErrorPage title={getTitle()} description={getDescription()} />;
        }

        if (loading || !options || !configuration) {
            return <FullScreenSpinner localizedMessageKey={"spinner.fetching"} noFade={true} />;
        }

        return (
            <MarketplaceThemeProvider
                applicationKey={applicationKey || ""}
                configuration={configuration}
                embedMode={false}
                launchedViaCustomerPortal={fromCustomerPortal === "true"}
                presentmentCurrency={presentmentCurrency}
                options={options}
            />
        );
    };
};
